import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ShieldAlert, Landmark } from "lucide-react";

/**
 * Guard for `/admin/*`.
 *
 * Only `platform_admin` and `founder` users may enter the platform back-office.
 * All other authenticated users are redirected to `/app/assistant` with a toast
 * hint. Guests go through the standard login flow.
 */
export default function AdminProtectedRoute({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F1214]">
        <div className="flex items-center gap-3 text-white/70">
          <Landmark className="h-5 w-5 animate-pulse text-accent" />
          <span className="font-heading text-sm">Chargement de l&apos;espace administrateur…</span>
        </div>
      </div>
    );
  }
  if (status === "guest") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  const role = user?.platform_role;
  const isPlatformStaff = role === "platform_admin" || role === "founder";
  if (!isPlatformStaff) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
        <ShieldAlert className="h-10 w-10 text-destructive" />
        <h1 className="mt-4 font-serif text-3xl">Accès refusé</h1>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          Cet espace est réservé au personnel Fonbex. Vos droits actuels
          ne vous autorisent pas à accéder au back-office.
        </p>
        <a
          href="/app/assistant"
          className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground"
          data-testid="admin-denied-back"
        >
          Retour à mon espace
        </a>
      </div>
    );
  }
  return children;
}
